<template>
  <div
    v-show="text"
    class="badge"
    :style="{ backgroundColor: 'rgba(244,82,147,0.1)', color: discountTimer?.textColor }"
  >
    <span class="timer-icon">
      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M5.4375 0C5.12684 0 4.875 0.25184 4.875 0.5625C4.875 0.87316 5.12684 1.125 5.4375 1.125H6.5625C6.87316 1.125 7.125 0.87316 7.125 0.5625C7.125 0.25184 6.87316 0 6.5625 0H5.4375ZM10.125 6.75C10.125 9.02817 8.27817 10.875 6 10.875C3.72183 10.875 1.875 9.02817 1.875 6.75C1.875 4.47183 3.72183 2.625 6 2.625C8.27817 2.625 10.125 4.47183 10.125 6.75ZM11.25 6.75C11.25 9.6495 8.8995 12 6 12C3.10051 12 0.75 9.6495 0.75 6.75C0.75 3.85051 3.10051 1.5 6 1.5C7.24712 1.5 8.39268 1.93484 9.29332 2.66118L9.94552 2.00899C10.1652 1.78932 10.5213 1.78932 10.741 2.00899C10.9607 2.22866 10.9607 2.58482 10.741 2.80449L10.0888 3.45668C10.8152 4.35732 11.25 5.50288 11.25 6.75ZM6.5625 4.31456C6.5625 4.0039 6.31066 3.75206 6 3.75206C5.68934 3.75206 5.4375 4.0039 5.4375 4.31456V6.94043C5.4375 7.25109 5.68934 7.50293 6 7.50293C6.31066 7.50293 6.5625 7.25109 6.5625 6.94043V4.31456Z"
          :fill="discountTimer?.textColor || '#F4488D'"
        />
      </svg>
    </span>
    <span class="label">
      {{ text }}
    </span>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import type { ProductPageQuery } from '@/shared/apollo-api/__generated__/types';

interface Props {
  discountTimer?: NonNullable<ProductPageQuery['product']['skuList'][0]['discountTimer']>;
}

const props = defineProps<Props>();

const text = ref('');
let intervalId: NodeJS.Timeout | null = null;

const label = computed(() => {
  return props.discountTimer?.text;
});

const getCustomTime = (time: number, timeType = 'default'): string => {
  const timeDescription = {
    hours: ' ч. ',
    minutes: ' мин. ',
    seconds: ' сек. ',
    default: '',
  };

  return (time >= 10 ? String(time) : `0${time}`) + timeDescription[timeType as keyof typeof timeDescription];
};

const getTimeLeft = (): string | null => {
  if (!props.discountTimer) return null;
  
  const timeLeft = props.discountTimer.endDate - Date.now();
  const hours = Math.floor(timeLeft / (1000 * 60 * 60));
  const minutes = Math.floor((timeLeft / (1000 * 60)) % 60);
  const seconds = Math.floor((timeLeft / 1000) % 60);
  let h = getCustomTime(hours, 'hours');
  let m = getCustomTime(minutes, 'minutes');
  let s = getCustomTime(seconds, 'seconds');

  if (hours > 0) return h + m;
  if (minutes > 0) return m + s;
  if (seconds > 0) return s;
  return null;
};

onMounted(() => {
  if (props.discountTimer) {
    intervalId = setInterval(() => {
      text.value = getTimeLeft() || '';
    }, 1000);
  }
});

onUnmounted(() => {
  if (intervalId) {
    clearInterval(intervalId);
  }
});
</script>

<style lang="stylus" scoped>
.badge
  height 20px
  border-radius 10px
  display flex
  align-items center
  justify-content center
  padding 3px 10px 3px 7px
  .label
    font-weight 500
    font-size 12px
    line-height 120%

.timer-icon
  margin 4px
</style>
